import { MessageSquare } from 'lucide-react';
import type { MouseEvent, ReactNode } from 'react';
import { REMARK_VISIBILITY_LABELS, initialsOf, remarkLine, type RemarkSummary } from '@nest-recruit/shared';
import { Kbd } from '../../../components/Kbd';
import { formatDateTime } from '../../../lib/format';
import { RemarkDot } from './RemarkTags';
import styles from './remarks.module.css';

// F23.13 (ADR 0049): RNK-01's Remark column — the latest remark the viewer may see, or "Remark" to add one.
// Either opens the remark box inside the list, under this row (DECISIONS-LOG 2026-10-17 "match the mockup" Q2).

interface RemarkCellProps {
  readonly summary: RemarkSummary;
  readonly name: string;
  readonly isPending: boolean;
  readonly canAdd: boolean;
  readonly onOpen: () => void;
}

export function RemarkCell({ summary, name, isPending, canAdd, onOpen }: RemarkCellProps): ReactNode {
  const latest = summary.latest;
  const open = (event: MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    onOpen();
  };
  if (!latest) {
    if (!canAdd) return <span aria-label="No remarks">—</span>;
    return (
      <button type="button" className={styles.cell} data-empty data-pending={isPending || undefined} aria-label={`Add a remark on ${name}`} onClick={open}>
        <MessageSquare size={14} aria-hidden />
        Remark
        <Kbd>M</Kbd>
      </button>
    );
  }
  const line = remarkLine(latest);
  const title = `${latest.authorName ?? '—'} · ${formatDateTime(latest.at)} · ${REMARK_VISIBILITY_LABELS[latest.visibility]}`;
  return (
    <button
      type="button"
      className={styles.cell}
      data-pending={isPending || undefined}
      title={title}
      aria-label={`${summary.count === 1 ? 'Remark' : `${summary.count} remarks`} on ${name}: ${line}. ${canAdd ? 'Add another.' : ''}`}
      onClick={open}
      disabled={!canAdd}
    >
      <RemarkDot tags={latest.tags} />
      <span className={styles.cellText}>{line}</span>
      {summary.count > 1 && <span className={styles.cellCount}>+{summary.count - 1}</span>}
      <span className={styles.cellAuthor}>{initialsOf(latest.authorName)}</span>
    </button>
  );
}
